// AE 打包檔接到頁面上：這個專案每個版型的打包檔清單、上傳進度、刪除。製作的版型管理和動畫師工作站共用。
//   list === null && loaded → 這個後台還沒有打包檔功能（舊版後台）：畫面不顯示這個功能
//   uploads[templateId]    正在上傳的那一包（進度；失敗的錯誤說明）
import { useCallback, useEffect, useRef, useState } from 'react';
import { aePackageErrorText, deleteAePackage, listAePackages, uploadAePackage, type AePackage, type AePackageList } from './aePackageApi';
import { aePackageFileProblem } from './aePackage';

export interface AeUploadState { filename: string; loaded: number; total: number; error: string | null }

export interface AePackagesState {
  loaded: boolean;
  /** null = 舊版後台沒有這個功能 */
  list: AePackageList | null;
  /** 讀取失敗的說明 */
  error: string | null;
  uploads: Record<string, AeUploadState>;
  packageOf: (templateId: string) => AePackage | null;
  reload: () => Promise<void>;
  /** 上傳 / 換新；回 null = 成功（或已經開始），字串 = 失敗的說明 */
  upload: (templateId: string, file: File) => Promise<string | null>;
  remove: (pkg: AePackage) => Promise<string | null>;
}

export function useAePackages(projectId: string | undefined, enabled = true): AePackagesState {
  const [list, setList] = useState<AePackageList | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<Record<string, AeUploadState>>({});
  const alive = useRef(true);
  const maxBytes = list?.maxBytes;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const reload = useCallback(async () => {
    if (!projectId || !enabled) return;
    try {
      const l = await listAePackages(projectId);
      if (!alive.current) return;
      setList(l);
      setError(null);
    } catch (e) {
      if (alive.current) setError(aePackageErrorText(e));
    } finally {
      if (alive.current) setLoaded(true);
    }
  }, [projectId, enabled]);

  useEffect(() => { setList(null); setLoaded(false); setUploads({}); void reload(); }, [reload]);

  const upload = useCallback(async (templateId: string, file: File) => {
    if (!projectId) return '還沒選專案';
    const problem = aePackageFileProblem(file, maxBytes);
    if (problem) return problem;
    setUploads(u => ({ ...u, [templateId]: { filename: file.name, loaded: 0, total: file.size, error: null } }));
    try {
      await uploadAePackage(projectId, templateId, file, {
        onProgress: (loaded, total) => { if (alive.current) setUploads(u => (u[templateId] ? { ...u, [templateId]: { ...u[templateId], loaded, total } } : u)); },
      });
      if (alive.current) setUploads(u => { const { [templateId]: _gone, ...rest } = u; void _gone; return rest; });
      await reload();
      return null;
    } catch (e) {
      const text = aePackageErrorText(e);
      if (alive.current) setUploads(u => { const { [templateId]: _gone, ...rest } = u; void _gone; return rest; });
      return text;
    }
  }, [projectId, maxBytes, reload]);

  const remove = useCallback(async (pkg: AePackage) => {
    if (!projectId) return '還沒選專案';
    try {
      await deleteAePackage(projectId, pkg.id);
      await reload();
      return null;
    } catch (e) {
      return aePackageErrorText(e);
    }
  }, [projectId, reload]);

  const packageOf = useCallback((templateId: string) => list?.packages.find(p => p.templateId === templateId) ?? null, [list]);

  return { loaded, list, error, uploads, packageOf, reload, upload, remove };
}
